import {
  getTierConfig,
  getTierRangeForGroup,
  niveauCodeToTier,
} from '../content/taxonomy/tiers'
import { GroupId } from '../config/systemConfig'

export interface CitoLevel {
  level: 'I' | 'II' | 'III' | 'IV' | 'V'
  label: string
  color: string
  bgColor: string
  borderColor: string
}

const CITO_COLORS = {
  I: {
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
  },
  II: {
    color: 'text-sky-700',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
  },
  III: {
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  IV: {
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
  },
  V: {
    color: 'text-rose-700',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
  },
}

/**
 * Maps a tier to a CITO level (I-V) relative to the student's group.
 *
 * @param tier The canonical tier number (1-16)
 * @param group The student's group (6, 7, 8)
 */
export function tierToCitoLevel(tier: number, group: GroupId): CitoLevel {
  const range = getTierRangeForGroup(group)

  // Cross-group handling: if tier is outside the expected range for the group
  if (tier < range.min) {
    return { level: 'V', label: 'CITO V', ...CITO_COLORS.V }
  }
  if (tier > range.max) {
    return { level: 'I', label: 'CITO I', ...CITO_COLORS.I }
  }

  const config = getTierConfig(tier)
  if (!config) {
    // Fallback if tier config is missing
    return { level: 'III', label: 'CITO III', ...CITO_COLORS.III }
  }

  let level: 'I' | 'II' | 'III' | 'IV' | 'V' = 'III'

  if (group === 6 || group === 7) {
    // 6 tiers per group
    if (config.period === 'B' && !config.isPlus) level = 'V'
    else if (config.period === 'B' && config.isPlus) level = 'IV'
    else if (config.period === 'M')
      level = 'III' // covers M and M+
    else if (config.period === 'E' && !config.isPlus) level = 'II'
    else if (config.period === 'E' && config.isPlus) level = 'I'
  } else if (group === 8) {
    // 4 tiers for group 8
    if (config.period === 'B') level = 'IV'
    else if (config.period === 'M') level = 'III'
    else if (config.period === 'E' && !config.isPlus) level = 'II'
    else if (config.period === 'E' && config.isPlus) level = 'I'
  }

  return {
    level,
    label: `CITO ${level}`,
    ...CITO_COLORS[level],
  }
}

/**
 * Convenience wrapper to map a level code (e.g. 'M7+') to a CITO level.
 */
export function levelCodeToCitoLevel(
  levelCode: string,
  group: GroupId,
): CitoLevel | null {
  const tier = niveauCodeToTier(levelCode)
  if (tier === undefined) return null
  return tierToCitoLevel(tier, group)
}
